'use client'

import React from 'react'

import { usePortal } from '../../../hooks'
import { ModalProps } from '../Modal'

interface RequiredModalProps {
  open: ModalProps['open']
  onClose: ModalProps['onClose']
  onCloseEnd: ModalProps['onCloseEnd']
}

type ModalOptions<P> = Omit<P, 'open'>

type ModalComponent<P> = React.ComponentType<RequiredModalProps & P>

interface ModalContextValue {
  // TODO: Improve typings to make sure the modal component has required props
  openModal: <P>(
    Modal: ModalComponent<P>,
    modalOptions: ModalOptions<P>,
  ) => void
}

interface ModalState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Modal: ModalComponent<any>
  open: boolean
  modalOptions?: Record<string, unknown>
}

const uninitialized = () => {
  throw new Error('ModalProvider not initialized')
}

const ModalContext = React.createContext<ModalContextValue>({
  openModal: uninitialized,
})

export interface ModalProviderProps {
  disablePortal?: boolean
  children: React.ReactNode
}

export const ModalProvider = ({
  disablePortal,
  children,
}: ModalProviderProps) => {
  const [modalState, setModalState] = React.useState<ModalState | null>(null)

  const { Portal } = usePortal()

  const openModal = React.useCallback<ModalContextValue['openModal']>(
    (Modal, modalOptions) => {
      setModalState({
        Modal,
        open: true,
        modalOptions,
      })
    },
    [],
  )

  const handleClose = React.useCallback(() => {
    setModalState((p) => (p ? { ...p, open: false } : null))
  }, [])

  const handleClosed = React.useCallback(() => {
    setModalState(null)
  }, [])

  const value = React.useMemo(() => ({ openModal }), [openModal])

  const { Modal, open, modalOptions } = modalState ?? {}

  const modalContent = Modal ? (
    <Modal
      {...modalOptions}
      open={open}
      onClose={() => {
        handleClose()
        ;(modalOptions as { onClose?: () => void })?.onClose?.()
      }}
      onCloseEnd={() => {
        handleClosed()
        ;(modalOptions as { onCloseEnd?: () => void })?.onCloseEnd?.()
      }}
    />
  ) : null

  return (
    <ModalContext.Provider value={value}>
      {children}
      {!disablePortal ? <Portal>{modalContent}</Portal> : modalContent}
    </ModalContext.Provider>
  )
}

export const useModal = () => {
  return React.useContext(ModalContext)
}
